/**
 * fieldTree — path-addressed read/write/remove/insert helpers over the
 * builder's field array.
 *
 * WHY
 * ---
 * `formArray` (and a Container/FieldSet field's own `.controls`) is a plain
 * array of field objects. Every existing call site addresses a field by a
 * single flat index into `formArray`. The `Container` field type introduces
 * one level of nesting — a child field now lives at
 * `formArray[i].controls[j]` — so a single index is no longer enough to say
 * "this field, wherever it lives".
 *
 * A `path` is an array of indices describing how to walk from the root
 * array down to a field: `[3]` is `formArray[3]`; `[3, 1]` is
 * `formArray[3].controls[1]`. A depth-1 path behaves exactly like a plain
 * index always has — these helpers don't change any existing flat-array
 * behavior, they just also handle deeper paths.
 *
 * All helpers are pure: they return a new array and never mutate the one
 * they were given (or anything reachable from it) — same convention as
 * removeFormFieldCascade.js / wireFields.js.
 *
 * SCOPE (v1): the builder only ever creates paths of depth 1 or 2 (a
 * Container can't be dropped inside another Container) — but nothing here
 * assumes that; a path of any depth works, so this doesn't need to change
 * if that restriction is ever lifted.
 */

/**
 * Read the field at `path`, or undefined if the path doesn't resolve.
 * @param {Array<Object>} tree
 * @param {Array<number>} path
 * @returns {Object|undefined}
 */
export function getAtPath(tree, path) {
  let node = { controls: tree };
  for (const i of path) {
    if (!node || !Array.isArray(node.controls)) return undefined;
    node = node.controls[i];
  }
  return node;
}

/**
 * Return a new tree with the field at `path` replaced. `updater` is either
 * the new field object, or a function `(oldField) => newField`.
 * No-op (returns `tree` by reference) if `path` is empty or doesn't resolve.
 * @param {Array<Object>} tree
 * @param {Array<number>} path
 * @param {Object|Function} updater
 * @returns {Array<Object>}
 */
export function setAtPath(tree, path, updater) {
  if (!Array.isArray(tree) || path.length === 0) return tree;
  const [head, ...rest] = path;
  if (head < 0 || head >= tree.length) return tree;

  const next = [...tree];
  if (rest.length === 0) {
    next[head] = typeof updater === "function" ? updater(next[head]) : updater;
    return next;
  }

  const child = next[head];
  if (!child || !Array.isArray(child.controls)) return tree;
  next[head] = { ...child, controls: setAtPath(child.controls, rest, updater) };
  return next;
}

/**
 * Return a new tree with the field at `path` removed, renumbering the
 * `order` of its remaining siblings to 1..N (matching the reindex
 * convention every existing reorder/remove call site already uses).
 * No-op (returns `tree` by reference) if `path` is empty or doesn't resolve.
 * @param {Array<Object>} tree
 * @param {Array<number>} path
 * @returns {Array<Object>}
 */
export function removeAtPath(tree, path) {
  if (!Array.isArray(tree) || path.length === 0) return tree;
  const [head, ...rest] = path;
  if (head < 0 || head >= tree.length) return tree;

  if (rest.length === 0) {
    return renumber([...tree.slice(0, head), ...tree.slice(head + 1)]);
  }

  const child = tree[head];
  if (!child || !Array.isArray(child.controls)) return tree;
  const next = [...tree];
  next[head] = { ...child, controls: removeAtPath(child.controls, rest) };
  return next;
}

/**
 * Return a new tree with `field` inserted at `index` among the children of
 * the node at `parentPath` (an empty `parentPath` means "insert at the
 * root"). Renumbers `order` of the affected sibling list to 1..N, same as
 * `removeAtPath`. `index` is clamped into range; `null`/`undefined` appends.
 * No-op (returns `tree` by reference) if `parentPath` doesn't resolve to a
 * node with a `.controls` array.
 * @param {Array<Object>} tree
 * @param {Array<number>} parentPath
 * @param {number|null|undefined} index
 * @param {Object} field
 * @returns {Array<Object>}
 */
export function insertAtPath(tree, parentPath, index, field) {
  if (!Array.isArray(tree)) return tree;

  if (parentPath.length === 0) {
    const pos = index == null ? tree.length : Math.min(Math.max(index, 0), tree.length);
    return renumber([...tree.slice(0, pos), field, ...tree.slice(pos)]);
  }

  const [head, ...rest] = parentPath;
  if (head < 0 || head >= tree.length) return tree;
  const parent = tree[head];
  if (!parent || !Array.isArray(parent.controls)) return tree;

  const next = [...tree];
  next[head] = { ...parent, controls: insertAtPath(parent.controls, rest, index, field) };
  return next;
}

/**
 * Returns a flat array of every field in `tree` PLUS every Container/
 * FieldSet's own `.controls` children, one level down (nesting never goes
 * deeper than that today — see the file header). Each entry is the SAME
 * object reference held in `tree` (or a node's own `.controls`), so
 * mutating an entry mutates the tree it came from too — useful for a
 * cleanup/scan pass that needs to reach "every field, wherever it lives"
 * without needing a path back to it (unlike findFieldByNameAnywhere/
 * findAllFieldsByType below, which return `{field, path}` for callers that
 * need to write back via setAtPath). Replaces what used to be several
 * independent, hand-written `tree.flatMap(f => f.controls ? [f, ...f.controls] : [f])`
 * copies across FormBuilder.js/removeFormFieldCascade.js.
 * @param {Array<Object>} tree
 * @returns {Array<Object>}
 */
export function flattenOneLevel(tree) {
  return tree.flatMap((item) => (Array.isArray(item.controls) ? [item, ...item.controls] : [item]));
}

/**
 * `Map<name, field>` over every field in `tree`, including one level into
 * any Container's own children (built on `flattenOneLevel` above). Entries
 * are the SAME object references held in `tree`, so mutating a value read
 * back out of this map (the established pattern at every call site) mutates
 * the tree it came from too.
 *
 * Was previously three independent, hand-written copies of this exact loop
 * (submissionDynamicform.js's fieldSelectChanged/fieldMultiSelectChanged/
 * formArrayMap) — one shared home means a future nesting-rule change (e.g. a
 * second nesting level, or excluding a new field type from Container
 * children) only needs to be made once.
 * @param {Array<Object>} tree
 * @returns {Map<string, Object>}
 */
export function buildFieldIndexMap(tree) {
  return new Map(flattenOneLevel(tree).map((item) => [item.name, item]));
}

function renumber(list) {
  return list.map((item, i) => ({ ...item, order: i + 1 }));
}

/**
 * Finds a field by `name`, searching top-level `tree` first and then one
 * level into any node's own `.controls` (a Container or FieldSet's
 * children). A field's `name` is globally unique across the whole form
 * (calc rules, show/hide rules and duplicate-check collection already rely
 * on this), so for every field that was already reachable by a flat
 * top-level search this returns the exact same match — it only ADDS the
 * ability to find a field living inside a container.
 *
 * Two passes at every level, on purpose: a match AT this level is checked
 * across every sibling before descending into ANY of their `.controls`.
 * A single combined loop (check tree[i].name, then immediately recurse into
 * tree[i].controls, then move to i+1) would let an early sibling's NESTED
 * match win over a same-named field elsewhere at this same level, purely
 * because of array order — e.g. a name an embedded FormGroup happens to
 * reuse, sitting in an earlier FieldSet, would shadow a later top-level
 * field of that same name. Global name-uniqueness means two passes changes
 * nothing for the expected (no-collision) case; it only makes the rare
 * collision case resolve to the shallower, unambiguously-more-correct match
 * instead of an order-dependent one.
 * @param {Array<Object>} tree
 * @param {string} name
 * @returns {{ field: Object, path: Array<number> } | null}
 */
export function findFieldByNameAnywhere(tree, name) {
  for (let i = 0; i < tree.length; i++) {
    if (tree[i].name === name) return { field: tree[i], path: [i] };
  }
  for (let i = 0; i < tree.length; i++) {
    if (Array.isArray(tree[i].controls)) {
      // Genuinely recursive, matching getAtPath/setAtPath/removeAtPath/
      // insertAtPath above — v1 only ever nests one level deep (a Container
      // can't be dropped inside another Container), but this doesn't
      // hardcode that assumption the way an unrolled one-level lookup
      // would, so it stays correct if that restriction is ever lifted.
      const nested = findFieldByNameAnywhere(tree[i].controls, name);
      if (nested) return { field: nested.field, path: [i, ...nested.path] };
    }
  }
  return null;
}

/**
 * Finds every field of a given `type` anywhere in `tree` — top-level and one
 * level into any node's own `.controls` (a Container's children). Same
 * one-level-deep contract as findFieldByNameAnywhere and for the same
 * reason (v1 only ever nests a field one level deep, inside a Container).
 * Used where a flat top-level `.filter(f => f.type === type)` needs to also
 * see a Container-nested match, e.g. collecting every GridComponent on a
 * form before a submit-time sync.
 * @param {Array<Object>} tree
 * @param {string} type
 * @returns {Array<{ field: Object, path: Array<number> }>}
 */
export function findAllFieldsByType(tree, type) {
  const results = [];
  for (let i = 0; i < tree.length; i++) {
    if (tree[i].type === type) results.push({ field: tree[i], path: [i] });
    if (Array.isArray(tree[i].controls)) {
      // Genuinely recursive, same rationale as findFieldByNameAnywhere above.
      for (const nested of findAllFieldsByType(tree[i].controls, type)) {
        results.push({ field: nested.field, path: [i, ...nested.path] });
      }
    }
  }
  return results;
}
